import React, { useState, useEffect, useRef } from 'react';
import { Button, Card, Typography, Row, Col, Image, Modal, Input, Upload, message, Popconfirm, Space, Collapse, Tag, Select, Spin } from 'antd';
import { PlusOutlined, UploadOutlined, DeleteOutlined, SearchOutlined, LoadingOutlined } from '@ant-design/icons';
import { internalApi } from '../../common/axios';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_URL;

const LunchFairy = () => {
    const [stores, setStores] = useState({});
    const [selectedStore, setSelectedStore] = useState(null);
    const excludedStoresRef = useRef([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [thinkingVisible, setThinkingVisible] = useState(false);
    const [newStore, setNewStore] = useState('');
    const [fileList, setFileList] = useState([]);
    const [memo, setMemo] = useState('');
    const [tags, setTags] = useState([]);
    const [editStore, setEditStore] = useState('');
    const [loading, setLoading] = useState(false);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [selectedTag, setSelectedTag] = useState('');
    const [isThinking, setIsThinking] = useState(false);

    useEffect(() => {
        void fetchStores();
    }, []);

    const fetchStores = async () => {
        try {
            const res = await internalApi.get('/lunchFairy');
            const storesWithDefaults = Object.fromEntries(
                Object.entries(res.data).map(([key, value]) => [
                    key,
                    {
                        ...value,
                        memo: value.memo || '',
                        tags: value.tags || []
                    }
                ])
            );
            setStores(storesWithDefaults);
        } catch (err) {
            message.error('스토어 정보를 불러오는데 실패했습니다.');
        }
    };

    const pickStore = (useExcludeList = false) => {
        if (useExcludeList && selectedStore && !excludedStoresRef.current.includes(selectedStore)) {
            excludedStoresRef.current.push(selectedStore);
        }
        if (!useExcludeList) excludedStoresRef.current = [];

        setLoading(true);
        setThinkingVisible(true);

        const allKeys = Object.keys(stores);
        const filteredKeys = useExcludeList ? allKeys.filter(k => !excludedStoresRef.current.includes(k)) : allKeys;
        if (filteredKeys.length === 0) {
            setLoading(false);
            setThinkingVisible(false);
            message.error('남은 메뉴가 없습니다. 😭');
            return;
        }

        setTimeout(() => {
            const randomKey = filteredKeys[Math.floor(Math.random() * filteredKeys.length)];
            setSelectedStore(randomKey);
            setLoading(false);
            setThinkingVisible(false);
        }, 1000);
    };

    const showModal = (store = '') => {
        setEditStore(store);
        setNewStore(store);
        setFileList([]);
        setMemo(stores[store]?.memo || '');
        setTags(stores[store]?.tags || []);
        setIsModalVisible(true);
    };

    const handleOk = async () => {
        if (!newStore) {
            message.error('상호명을 입력해 주세요.');
            return;
        }

        const formData = new FormData();
        formData.append('store', newStore);
        fileList.forEach(file => {
            formData.append('images', file.originFileObj);
        });
        if (memo) formData.append('memo', memo);
        if (tags.length > 0) formData.append('tags', tags.join(','));

        try {
            const res = await internalApi.post('/lunchFairy/suggest', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            if (res.status !== 200) {
                message.error('제안 등록에 실패했습니다.');
                return;
            }
            message.success('메뉴가 성공적으로 추가되었습니다!');
            setIsModalVisible(false);
            setNewStore('');
            setFileList([]);
            setMemo('');
            setTags([]);
            void fetchStores();
        } catch (err) {
            message.error(err.message);
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setNewStore('');
        setFileList([]);
    };

    const handleDeleteImage = async (store, imagePath) => {
        try {
            await internalApi.post('/lunchFairy/remove', { store, imagePath });
            message.success('이미지를 삭제했습니다.');
            void fetchStores();
        } catch (err) {
            message.error('삭제에 실패했습니다.');
        }
    };

    const handleDeleteStore = async (store) => {
        try {
            await internalApi.post('/lunchFairy/deleteStore', { store });
            message.success('상호를 삭제했습니다.');
            void fetchStores();
        } catch (err) {
            message.error('상호 삭제에 실패했습니다.');
        }
    };

    const filteredStoreKeys = Object.keys(stores).filter((storeName) => {
        const store = stores[storeName];
        const matchesKeyword = storeName.includes(searchKeyword);
        const matchesTag = selectedTag ? store.tags.includes(selectedTag) : true;
        return matchesKeyword && matchesTag;
    });

    const allTags = [...new Set(Object.values(stores).flatMap(store => store.tags))];

    return (
        <>
            <Modal open={thinkingVisible} footer={null} closable={false} centered maskClosable={false}
                   style={{textAlign: 'center'}}>
                <div style={{position: 'relative', display: 'inline-block'}}>
                    <Spin
                        indicator={<LoadingOutlined style={{fontSize: 250, color: 'rgba(0, 0, 0, 0.15)' }} spin/>} // 더 크게
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 1
                        }}
                    />
                    <Image
                        preview={false}
                        src={`${IMAGE_BASE_URL}/images/thinking-pig.jpg`}
                        alt="thinking"
                        style={{display: 'block'}}
                    />
                </div>
                <p style={{marginTop: 12}}>점메추 요정이 고민 중이에요...</p>
            </Modal>

            <Row justify="center" align="top" style={{padding: 24, backgroundColor: '#fff0f6'}}>
                <Col span={24} style={{maxWidth: 800}}>
                    <Card variant="outlined" style={{textAlign: 'center', padding: '40px'}}>
                        <Title level={2}>🍱 점메추 요정 🍱</Title>
                        <Text style={{fontSize: '18px'}}>
                            {isThinking ? '점메추 요정이 고민 중이에요...' : selectedStore ? `오늘은 "${stores[selectedStore].label}" 어떤가요?` : '요정이 상호를 고민 중이에요...'}
                        </Text>
                        <br/><br/>
                        <Space wrap>
                            <Button type="primary" size="large" onClick={() => pickStore(false)} loading={loading}>
                                {selectedStore ? (loading ? '새로 돌리는 중 🔄' : '새로 돌리기 🔁') : '추천해줘!'}
                            </Button>
                            {selectedStore && (
                                <Button size="large" onClick={() => pickStore(true)}>
                                    제외하고 새로 돌리기 🚫
                                </Button>
                            )}
                        </Space>
                        <br /><br />
                        {
                            isThinking && (
                                <Image
                                    preview={false}
                                    src={`${IMAGE_BASE_URL}/images/thinking-pig.jpg`}
                                    alt="thinking"
                                />
                            )}
                        {!isThinking && selectedStore && (
                            <Image.PreviewGroup>
                                <Row justify="center">
                                    {stores[selectedStore].menuList.map((src, idx) => (
                                        <Col key={idx}>
                                            <Image
                                                style={{ maxWidth: '100vw', maxHeight: '90vh', cursor: 'pointer' }}
                                                preview={{ mask: '클릭하여 확대', maskClassName: 'custom-mask' }}
                                                src={`${IMAGE_BASE_URL}${src}`}
                                                alt={`메뉴 ${idx + 1}`}
                                            />
                                        </Col>
                                    ))}
                                    <Col span={24} style={{ marginTop: 16, textAlign: 'center' }}>
                                        <Text style={{ display: 'block', marginBottom: 8 }}>
                                            {stores[selectedStore].memo || '메모 없음'}
                                        </Text>
                                        <Space wrap>
                                            {(stores[selectedStore].tags || []).length > 0 ? (
                                                stores[selectedStore].tags.map((tag, i) => (
                                                    <Tag key={i}>#{tag}</Tag>
                                                ))
                                            ) : (
                                                <Text type="secondary">태그 없음</Text>
                                            )}
                                        </Space>
                                    </Col>
                                </Row>
                            </Image.PreviewGroup>
                        )}
                    </Card>

                    <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={() => showModal('')}
                        style={{ marginTop: 48, marginBottom: 24 }}
                        block
                    >
                        상호 및 메뉴 제안하기
                    </Button>

                    {/* 검색 및 태그 필터 */}
                    <Space style={{ marginBottom: 16 }} wrap>
                        <Input
                            placeholder="상호명 검색"
                            prefix={<SearchOutlined />}
                            value={searchKeyword}
                            onChange={(e) => setSearchKeyword(e.target.value)}
                            style={{ width: 200 }}
                        />
                        <Space>
                            {allTags.map((tag, i) => (
                                <Tag
                                    key={i}
                                    color={tag === selectedTag ? 'blue' : 'default'}
                                    onClick={() => setSelectedTag(tag === selectedTag ? '' : tag)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    #{tag}
                                </Tag>
                            ))}
                        </Space>
                    </Space>

                    <Collapse defaultActiveKey={[]}>
                        <Panel header={<Title level={3} style={{ margin: 0 }}>전체 메뉴 관리</Title>} key="menu-management">
                            {filteredStoreKeys.map(storeName => (
                                <Collapse key={storeName} defaultActiveKey={[]} style={{ marginBottom: 16 }}>
                                    <Panel
                                        header={storeName}
                                        key="store"
                                        extra={
                                            <Space>
                                                <Button size="small" icon={<PlusOutlined />} onClick={(e) => { e.stopPropagation(); showModal(storeName); }} />
                                                <Popconfirm
                                                    title="정말 상호를 삭제할까요?"
                                                    onConfirm={(e) => { e.stopPropagation(); handleDeleteStore(storeName); }}
                                                    okText="삭제"
                                                    cancelText="취소"
                                                >
                                                    <Button danger size="small" icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()} />
                                                </Popconfirm>
                                            </Space>
                                        }
                                    >
                                        <Row gutter={[16, 16]}>
                                            {stores[storeName].menuList.map((imgPath, idx) => (
                                                <Col key={idx}>
                                                    <Image
                                                        style={{ maxWidth: '50vw', maxHeight: '45vh', borderRadius: 8 }}
                                                        src={`${IMAGE_BASE_URL}${imgPath}`}
                                                        alt={`메뉴`}
                                                        preview={{ mask: '클릭하여 확대', maskClassName: 'custom-mask' }}
                                                    />
                                                    <Popconfirm
                                                        title="정말 삭제할까요?"
                                                        onConfirm={() => handleDeleteImage(storeName, imgPath)}
                                                        okText="삭제"
                                                        cancelText="취소"
                                                    >
                                                        <Button danger block size="small" style={{ marginTop: 8 }}>
                                                            삭제
                                                        </Button>
                                                    </Popconfirm>
                                                </Col>
                                            ))}
                                        </Row>
                                        <Input.TextArea
                                            readOnly
                                            autoSize={{ minRows: 1, maxRows: 3 }}
                                            value={stores[storeName].memo || '메모 없음'}
                                            style={{ marginTop: 16, resize: 'none' }}
                                        />
                                        <Space wrap style={{ marginTop: 4 }}>
                                            {(stores[storeName].tags || []).length > 0 ? (
                                                stores[storeName].tags.map((tag, i) => (
                                                    <Tag key={i}>#{tag}</Tag>
                                                ))
                                            ) : (
                                                <Text type="secondary">태그 없음</Text>
                                            )}
                                        </Space>
                                    </Panel>
                                </Collapse>
                            ))}
                        </Panel>
                    </Collapse>

                    <Modal
                        title="메뉴 제안하기"
                        open={isModalVisible}
                        onOk={handleOk}
                        onCancel={handleCancel}
                        okText="제안하기"
                        cancelText="취소"
                    >
                        <Input
                            placeholder="상호명을 입력하세요"
                            value={newStore}
                            onChange={(e) => setNewStore(e.target.value)}
                            style={{ marginBottom: '16px' }}
                        />
                        <Input
                            placeholder="메모를 입력하세요 (선택 입력)"
                            value={memo}
                            onChange={(e) => setMemo(e.target.value)}
                            style={{ marginBottom: '12px' }} />
                        <Select
                            mode="tags"
                            style={{ width: '100%', marginBottom: 12 }}
                            placeholder="태그를 입력하거나 선택하세요"
                            value={tags}
                            onChange={setTags}
                            tokenSeparators={[',']}
                        >
                            {allTags.map(tag => (
                                <Select.Option key={tag} value={tag}>
                                    {tag}
                                </Select.Option>
                            ))}
                        </Select>
                        <Upload
                            beforeUpload={() => false}
                            multiple
                            fileList={fileList}
                            onChange={({ fileList }) => setFileList(fileList)}
                            listType="picture"
                        >
                            <Button icon={<UploadOutlined />}>메뉴 이미지 업로드</Button>
                        </Upload>
                    </Modal>
                </Col>
            </Row>
        </>

    );
};

export default LunchFairy;
